import { useState, useEffect, useMemo } from 'react';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  Cloud,
  Thermometer,
  Droplets,
  CheckCircle,
  Clock,
  AlertTriangle,
  Save,
  FileText,
  Edit3,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/admin';
import { Button, Card, Spinner, Badge, Select } from '@/components/common';
import { diary as diaryApi } from '@/utils/api';
import api from '@/utils/api';
import { toast } from 'sonner';
import { useDiaryProjects, useDiaryWeek } from '@/hooks/useDiary';

// Helper: Get ISO week number
const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Helper: Get Monday of a given week (ISO 8601)
const getMondayOfWeek = (year, week) => {
  // January 4th is always in week 1
  const jan4 = new Date(year, 0, 4, 12, 0, 0); // Use noon to avoid timezone issues
  const jan4Day = jan4.getDay() || 7; // Convert Sunday from 0 to 7

  // Find Monday of week 1
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - jan4Day + 1);

  // Find Monday of requested week
  const monday = new Date(week1Monday);
  monday.setDate(week1Monday.getDate() + (week - 1) * 7);
  monday.setHours(12, 0, 0, 0); // Noon to avoid timezone issues

  return monday;
};

// Helper: Get all weekdays (Mo-Fr) for a week
const getWeekDays = (year, week) => {
  const monday = getMondayOfWeek(year, week);
  const days = [];
  for (let i = 0; i < 5; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    day.setHours(12, 0, 0, 0); // Noon to avoid timezone issues
    days.push(day);
  }

  // Debug: Log generated dates
  console.log(`📅 getWeekDays(${year}, ${week}):`, days.map(d => formatDateISO(d)));

  return days;
};

// Helper: Format date as YYYY-MM-DD (local timezone, not UTC!)
const formatDateISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper: Format date as DD.MM.YY
const formatDateDE = (date) => {
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

// Helper: Format date as DD.MM.
const formatDateShort = (date) => {
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
  });
};

// Day names
const DAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr'];

// Status labels and colors
const STATUS_CONFIG = {
  draft: { label: 'Entwurf', color: 'yellow', icon: Edit3 },
  reviewed: { label: 'Geprüft', color: 'blue', icon: Clock },
  approved: { label: 'Freigegeben', color: 'green', icon: CheckCircle },
};

// Bugtracker #96: zuletzt gewähltes Bauvorhaben merken (pro Browser)
const LAST_PROJECT_KEY = 'bautagebuch:lastProjectId';

const Bautagebuch = () => {
  // Bugtracker #114: Die API liefert Projekt-IDs als String — die Auswahl darf
  // NICHT in Number konvertiert werden, sonst schlägt der Existenz-Check unten
  // ('12' === 12 → false) fehl und die Auswahl springt aufs erste Projekt zurück.
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    return localStorage.getItem(LAST_PROJECT_KEY) || null;
  });
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week is rightmost
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editedEntry, setEditedEntry] = useState(null);
  const [saving, setSaving] = useState(false);
  // Bauleiter signatures for PDF export
  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState('');

  const queryClient = useQueryClient();

  // Calculate which weeks to display (3 weeks, current week on right)
  const weeksToShow = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentWeek = getISOWeek(today);

    const weeks = [];
    for (let i = 2; i >= 0; i--) {
      let weekNum = currentWeek - weekOffset - i;
      let year = currentYear;

      // Handle year boundaries
      if (weekNum <= 0) {
        year--;
        weekNum = 52 + weekNum; // Adjust for previous year
      } else if (weekNum > 52) {
        year++;
        weekNum = weekNum - 52;
      }

      weeks.push({ year, week: weekNum, isCurrent: i === 0 && weekOffset === 0 });
    }
    return weeks;
  }, [weekOffset]);

  // Load projects via TanStack Query
  const { data: projects = [], isLoading: projectsLoading } = useDiaryProjects();

  // Auto-select first project when projects load
  const resolvedProjectId = selectedProjectId ?? (projects.length > 0 ? projects[0].id : null);

  // Bugtracker #96: gespeicherte Auswahl verwerfen, falls das Projekt nicht (mehr) existiert
  useEffect(() => {
    if (selectedProjectId != null && projects.length > 0 && !projects.some((p) => String(p.id) === String(selectedProjectId))) {
      setSelectedProjectId(null);
      localStorage.removeItem(LAST_PROJECT_KEY);
    }
  }, [projects, selectedProjectId]);

  // Load signatures on mount (no TanStack Query hook available for this)
  useEffect(() => {
    diaryApi.getSignatures().then((res) => {
      setSignatures(res.data || []);
      if (res.data && res.data.length > 0) {
        setSelectedSignature(res.data[0].signatureFile);
      }
    }).catch((err) => console.error('Error loading signatures:', err));
  }, []);

  // Load all 3 weeks via individual useQuery calls (hooks must be unconditional)
  const week0 = weeksToShow[0];
  const week1 = weeksToShow[1];
  const week2 = weeksToShow[2];

  const { data: entries0 = [] } = useDiaryWeek(resolvedProjectId, week0.year, week0.week);
  const { data: entries1 = [] } = useDiaryWeek(resolvedProjectId, week1.year, week1.week);
  const { data: entries2 = [], isLoading: weekLoading } = useDiaryWeek(resolvedProjectId, week2.year, week2.week);

  const weekData = {
    [`${week0.year}-${week0.week}`]: entries0,
    [`${week1.year}-${week1.week}`]: entries1,
    [`${week2.year}-${week2.week}`]: entries2,
  };

  const loading = projectsLoading || weekLoading;

  const invalidateDiary = () => queryClient.invalidateQueries({ queryKey: ['diary'] });

  const saveEntryMutation = useMutation({
    mutationFn: (entry) => diaryApi.updateEntry(entry.id, {
      description_german: entry.description_german,
      incidents: entry.incidents,
      obstructions: entry.obstructions,
      worker_count: entry.worker_count,
    }),
    onSuccess: () => {
      toast.success('Änderungen gespeichert');
      setSelectedEntry({ ...editedEntry });
      invalidateDiary();
    },
    onError: (err) => toast.error(err.message || 'Fehler beim Speichern'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => diaryApi.updateStatus(id, status),
    onSuccess: (_, { status }) => {
      toast.success(`Status auf "${STATUS_CONFIG[status].label}" geändert`);
      setSelectedEntry({ ...selectedEntry, status });
      setEditedEntry({ ...editedEntry, status });
      invalidateDiary();
    },
    onError: (err) => toast.error(err.message || 'Fehler beim Statuswechsel'),
  });

  const handleDayClick = (date, entry) => {
    console.log('🖱️ Day clicked:', formatDateISO(date), 'Entry:', entry);
    if (entry) {
      console.log('🖱️ Setting selected entry:', entry.id);
      setSelectedEntry(entry);
      setEditedEntry({ ...entry });
    } else {
      console.log('🖱️ No entry for this day, clearing selection');
      setSelectedEntry(null);
      setEditedEntry(null);
    }
  };

  const handleSaveEntry = () => {
    if (!editedEntry) return;
    saveEntryMutation.mutate(editedEntry);
  };

  const handleStatusChange = (newStatus) => {
    if (!selectedEntry) return;
    statusMutation.mutate({ id: selectedEntry.id, status: newStatus });
  };

  const handlePdfExport = async () => {
    // Bug #79/#80: use resolvedProjectId (falls back to first project) — selectedProjectId
    // stays null until the user manually changes the dropdown, so for the auto-selected
    // first project the export button silently did nothing (no request, no console error).
    if (!selectedEntry || !resolvedProjectId) return;

    setSaving(true);
    try {
      // Build URL with optional signature parameter
      let exportUrl = `/diary/export/${resolvedProjectId}/${selectedEntry.year}/${selectedEntry.calendar_week}`;
      if (selectedSignature) {
        exportUrl += `?signatureFile=${encodeURIComponent(selectedSignature)}`;
      }

      const response = await api.get(exportUrl, { responseType: 'blob' });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Bautagebuch_KW${String(selectedEntry.calendar_week).padStart(2, '0')}_${selectedEntry.year}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF erfolgreich heruntergeladen');
    } catch (err) {
      console.error('PDF Export error:', err);
      toast.error(err.response?.data?.error || 'Fehler beim Erstellen der PDF');
    } finally {
      setSaving(false);
    }
  };

  // Get day status for calendar display
  const getDayStatus = (date, entries) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateNorm = new Date(date);
    dateNorm.setHours(0, 0, 0, 0);

    const dateStr = formatDateISO(date);
    const entry = entries.find((e) => e.date === dateStr);

    // Debug logging
    if (entries.length > 0) {
      console.log(`🔍 getDayStatus: Looking for ${dateStr} in`, entries.map(e => e.date), '→ Found:', !!entry);
    }

    if (entry) {
      if (entry.status === 'approved') {
        return { status: 'approved', entry, color: 'bg-green-500', text: 'text-green-600' };
      }
      return { status: 'pending', entry, color: 'bg-yellow-500', text: 'text-yellow-600' };
    }

    if (dateNorm > today) {
      return { status: 'future', entry: null, color: 'bg-gray-200', text: 'text-gray-400' };
    }

    return { status: 'missing', entry: null, color: 'bg-red-500', text: 'text-red-600' };
  };

  const selectedProject = projects.find((p) => String(p.id) === String(resolvedProjectId));

  // Debug: Log render state
  console.log('🎨 RENDER - projects state:', projects.length, 'items, resolvedProjectId:', resolvedProjectId);

  return (
    <div>
      <PageHeader
        title="Bautagebücher"
        description="Einträge der Bauleiter verwalten und prüfen"
        icon={BookOpen}
      />

      {/* Project and Signature Selector */}
      <Card className="mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="font-medium text-gray-700">Bauvorhaben:</label>
            {projects.length === 0 ? (
              <span className="text-gray-500 italic">Keine Bauvorhaben gefunden</span>
            ) : (
              <Select
                value={resolvedProjectId || ''}
                onChange={(e) => {
                  const pid = e.target.value;
                  setSelectedProjectId(pid);
                  localStorage.setItem(LAST_PROJECT_KEY, pid);
                  setSelectedEntry(null);
                  setEditedEntry(null);
                }}
                className="min-w-[280px]"
                placeholder="Bauvorhaben auswählen..."
                options={projects.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
              />
            )}
          </div>

          {/* Bauleiter Signature for PDF Export */}
          {signatures.length > 0 && (
            <div className="flex items-center gap-2">
              <label className="font-medium text-gray-700">PDF-Signatur:</label>
              <Select
                value={selectedSignature || ''}
                onChange={(e) => setSelectedSignature(e.target.value)}
                className="min-w-[180px]"
                placeholder="Bauleiter wählen..."
                options={[
                  { value: '', label: 'Keine Signatur' },
                  ...signatures.map((s) => ({
                    value: s.signatureFile,
                    label: s.name,
                  })),
                ]}
              />
            </div>
          )}

          <span className="text-xs text-gray-400">({projects.length} Projekte)</span>
        </div>
      </Card>

      {loading && !selectedProjectId ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {/* Week Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {weeksToShow.map(({ year, week, isCurrent }) => {
              const key = `${year}-${week}`;
              const entries = weekData[key] || [];
              const weekDays = getWeekDays(year, week);
              console.log(`🗓️ Rendering KW${week}/${year} - ${entries.length} entries, weekDays:`, weekDays.map(d => formatDateISO(d)));
              const startDate = formatDateShort(weekDays[0]);
              const endDate = formatDateShort(weekDays[4]);

              return (
                <Card
                  key={key}
                  className={`${isCurrent ? 'ring-2 ring-orange-500 ring-offset-2' : ''}`}
                >
                  <div className="text-center mb-4">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center justify-center gap-2">
                      KW{String(week).padStart(2, '0')}
                      {isCurrent && (
                        <Badge color="orange" size="sm">
                          Aktuell
                        </Badge>
                      )}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {startDate} - {endDate}
                    </p>
                  </div>

                  <div className="grid grid-cols-5 gap-2">
                    {weekDays.map((date, idx) => {
                      const { status, entry, color, text } = getDayStatus(date, entries);
                      const isSelected =
                        selectedEntry && entry && selectedEntry.id === entry.id;

                      return (
                        <button
                          key={idx}
                          onClick={() => handleDayClick(date, entry)}
                          disabled={false}
                          className={`
                            flex flex-col items-center p-2 rounded-lg transition-all
                            hover:bg-gray-100 cursor-pointer
                            ${isSelected ? 'bg-orange-100 ring-2 ring-orange-500' : ''}
                          `}
                        >
                          <span className={`text-xs font-medium ${text}`}>
                            {DAY_NAMES[idx]}
                          </span>
                          <div
                            className={`w-3 h-3 rounded-full mt-1 ${color}`}
                            title={
                              status === 'approved'
                                ? 'Freigegeben'
                                : status === 'pending'
                                  ? 'Entwurf/Geprüft'
                                  : status === 'missing'
                                    ? 'Fehlt'
                                    : 'Zukunft'
                            }
                          />
                          <span className="text-xs text-gray-500 mt-1">
                            {date.getDate()}.
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mb-6">
            <Button
              variant="outline"
              onClick={() => setWeekOffset(weekOffset + 1)}
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Früher
            </Button>
            <Button
              variant="outline"
              onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))}
              disabled={weekOffset === 0}
            >
              Später
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          {/* Detail View */}
          {selectedEntry && editedEntry && (
            <Card className="animate-in slide-in-from-top-4">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    {new Date(selectedEntry.date).toLocaleDateString('de-DE', {
                      weekday: 'long',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </h2>
                  <p className="text-gray-500">
                    KW{String(selectedEntry.calendar_week).padStart(2, '0')} |{' '}
                    {selectedProject?.name}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-600">Status:</label>
                  <Select
                    value={editedEntry.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="min-w-[150px]"
                    placeholder=""
                    options={[
                      { value: 'draft', label: 'Entwurf' },
                      { value: 'reviewed', label: 'Geprüft' },
                      { value: 'approved', label: 'Freigegeben' },
                    ]}
                  />
                </div>
              </div>

              {/* Weather & Workers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
                    <Cloud className="h-4 w-4" />
                    Wetter
                  </h4>
                  <div className="text-gray-700">
                    <p className="flex items-center gap-2">
                      <Thermometer className="h-4 w-4 text-gray-500" />
                      {selectedEntry.weather_temp_min !== null
                        ? `${selectedEntry.weather_temp_min}°C - ${selectedEntry.weather_temp_max}°C`
                        : 'Nicht verfügbar'}
                    </p>
                    {selectedEntry.weather_description && (
                      <p className="mt-1">{selectedEntry.weather_description}</p>
                    )}
                    {selectedEntry.weather_precipitation !== null && (
                      <p className="flex items-center gap-2 mt-1">
                        <Droplets className="h-4 w-4 text-gray-500" />
                        {selectedEntry.weather_precipitation} mm Niederschlag
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4" />
                    Mitarbeiter
                  </h4>
                  <input
                    type="number"
                    min="0"
                    max="999"
                    value={editedEntry.worker_count || 0}
                    onChange={(e) =>
                      setEditedEntry({
                        ...editedEntry,
                        worker_count: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>

              {/* Activities */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-800 flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4" />
                  Tätigkeiten
                </h4>
                <textarea
                  value={editedEntry.description_german || ''}
                  onChange={(e) =>
                    setEditedEntry({ ...editedEntry, description_german: e.target.value })
                  }
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Beschreibung der ausgeführten Arbeiten..."
                />
              </div>

              {/* Incidents & Obstructions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <h4 className="font-medium text-gray-800 flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    Vorkommnisse
                  </h4>
                  <textarea
                    value={editedEntry.incidents || ''}
                    onChange={(e) =>
                      setEditedEntry({ ...editedEntry, incidents: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Keine"
                  />
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    Behinderungen
                  </h4>
                  <textarea
                    value={editedEntry.obstructions || ''}
                    onChange={(e) =>
                      setEditedEntry({ ...editedEntry, obstructions: e.target.value })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Keine"
                  />
                </div>
              </div>

              {/* Original Text */}
              {selectedEntry.description_original && (
                <div className="mb-6 bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-600 text-sm mb-2">
                    Original-Text (Bauleiter):
                  </h4>
                  <p className="text-gray-500 italic text-sm whitespace-pre-wrap">
                    "{selectedEntry.description_original}"
                  </p>
                </div>
              )}

              {/* Meta Info */}
              <div className="text-sm text-gray-500 mb-6 flex flex-wrap gap-4">
                <span>
                  Bauleiter: <strong>{selectedEntry.bauleiter_name}</strong>
                </span>
                <span>
                  Eingetragen:{' '}
                  <strong>
                    {new Date(selectedEntry.created_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </strong>
                </span>
                {selectedEntry.reviewed_by && (
                  <span>
                    Geprüft von: <strong>{selectedEntry.reviewed_by}</strong> (
                    {new Date(selectedEntry.reviewed_at).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                    })}
                    )
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button onClick={handleSaveEntry} disabled={saveEntryMutation.isPending}>
                  <Save className="h-4 w-4 mr-2" />
                  {saveEntryMutation.isPending ? 'Speichern...' : 'Änderungen speichern'}
                </Button>
                {editedEntry.status === 'draft' && (
                  <Button
                    variant="secondary"
                    onClick={() => handleStatusChange('reviewed')}
                    disabled={statusMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Als geprüft markieren
                  </Button>
                )}
                <Button variant="outline" onClick={handlePdfExport}>
                  <FileText className="h-4 w-4 mr-2" />
                  PDF Export KW{String(selectedEntry.calendar_week).padStart(2, '0')}
                </Button>
              </div>
            </Card>
          )}

          {/* Empty State for Detail */}
          {!selectedEntry && (
            <Card className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                Klicke auf einen Tag, um den Eintrag anzuzeigen
              </p>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default Bautagebuch;
